import {
  Archive as ArchiveIcon,
  CalendarClock,
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Layers3,
  Pencil,
  RotateCcw,
  Trash2,
  Plus,
  Search,
  X,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type {
  FlashcardReviewCard,
  FlashcardReviewCollection,
  FlashcardReviewOutcome,
  FlashcardReviewOverview,
  Flashcard,
  FlashcardCollection,
  ManagedFlashcardStatusAction,
  ManagedFlashcardsResponse,
  ReviewFlashcardInput,
} from "@learn-flow/contracts";
import { createFlashcardCollectionInputSchema } from "@learn-flow/contracts";
import { type UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AppShell } from "../../components/app-shell.js";
import { sessionQueryKey } from "../auth/session.js";
import { FlashcardDialog } from "../activities/flashcard-dialog.js";
import { ApiError } from "../../lib/api.js";
import { createUuid } from "../../lib/random-uuid.js";
import {
  flashcardReviewOverviewKey,
  loadFlashcardReviewOverview,
  loadManagedFlashcards,
  managedFlashcardsKey,
  searchedFlashcardsKey,
  searchManagedFlashcards,
  saveFlashcardReview,
  updateManagedFlashcard,
  deleteManagedFlashcard,
  createFlashcardCollection,
  updateManagedFlashcardStatus,
} from "./flashcards-api.js";
import { createReviewSnapshot } from "./flashcards-preview-model.js";

type OutcomeCounts = Record<FlashcardReviewOutcome, number>;

type ReviewSession = Readonly<{
  collectionId: string | null;
  collectionName: string;
  cards: readonly FlashcardReviewCard[];
  index: number;
  outcomes: OutcomeCounts;
}>;

type CompletedReview = Readonly<{
  collectionId: string | null;
  collectionName: string;
  total: number;
  outcomes: OutcomeCounts;
}>;

type ReviewSubmission = Readonly<{
  card: FlashcardReviewCard;
  outcome: FlashcardReviewOutcome;
  idempotencyKey: ReviewFlashcardInput["idempotencyKey"];
}>;

const emptyOutcomeCounts = (): OutcomeCounts => ({
  "keep-current": 0,
  "one-week": 0,
  "one-month": 0,
  "three-months": 0,
  archive: 0,
});

export function FlashcardsPage(): React.JSX.Element {
  const [parameters, setSearchParameters] = useSearchParams();
  const queryClient = useQueryClient();
  const urlSearch = (parameters.get("search") ?? "").slice(0, 120).trim();
  const [searchValue, setSearchValue] = useState(urlSearch);
  const [searchPage, setSearchPage] = useState(1);
  const overviewQuery = useQuery({
    queryKey: flashcardReviewOverviewKey,
    queryFn: loadFlashcardReviewOverview,
    retry: false,
  });
  const [session, setSession] = useState<ReviewSession | null>(null);
  const [completedReview, setCompletedReview] = useState<CompletedReview | null>(null);
  const [answerVisible, setAnswerVisible] = useState(false);
  const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [startError, setStartError] = useState("");
  const [startingCollectionId, setStartingCollectionId] = useState<string | null | undefined>();
  const [failedSubmission, setFailedSubmission] = useState<ReviewSubmission | null>(null);
  const [organizing, setOrganizing] = useState<FlashcardReviewCollection | null>(null);
  const [organizePage, setOrganizePage] = useState(1);
  const [newCollectionOpen, setNewCollectionOpen] = useState(false);
  const [newFlashcardOpen, setNewFlashcardOpen] = useState(false);
  const newCollectionButtonRef = useRef<HTMLButtonElement>(null);
  const newFlashcardButtonRef = useRef<HTMLButtonElement>(null);
  const collectionStartRefs = useRef(new Map<string, HTMLButtonElement>());
  const collectionOrganizeRefs = useRef(new Map<string, HTMLButtonElement>());
  const launchingCollectionIdRef = useRef<string | null>(null);
  const reviewQuestionRef = useRef<HTMLHeadingElement>(null);
  const completionHeadingRef = useRef<HTMLHeadingElement>(null);
  const archiveOutcomeRef = useRef<HTMLButtonElement>(null);
  const advancingRef = useRef(false);
  const organizeHeadingRef = useRef<HTMLHeadingElement>(null);
  const managedQuery = useQuery({
    queryKey: managedFlashcardsKey(organizing?.id ?? null, organizePage),
    queryFn: () => loadManagedFlashcards(organizing?.id ?? null, organizePage),
    enabled: organizing !== null,
    retry: false,
  });
  const searchedQuery = useQuery({
    queryKey: searchedFlashcardsKey(urlSearch, searchPage),
    queryFn: () => searchManagedFlashcards(urlSearch, searchPage),
    enabled: urlSearch.length > 0,
    retry: false,
  });

  const updateSearchParameter = useCallback(
    (value: string) => {
      const next = new URLSearchParams(parameters);
      if (value) next.set("search", value);
      else next.delete("search");
      setSearchPage(1);
      setSearchParameters(next, { replace: true });
    },
    [parameters, setSearchParameters],
  );

  const saveMutation = useMutation({
    mutationFn: (submission: ReviewSubmission) =>
      saveFlashcardReview(submission.card.id, {
        outcome: submission.outcome,
        idempotencyKey: submission.idempotencyKey,
        expectedVersion: submission.card.reviewVersion,
      }),
  });

  useEffect(() => {
    document.title = "Flash Cards · MemoKite";
  }, []);

  useEffect(() => {
    setSearchValue(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    const trimmed = searchValue.trim();
    if (trimmed === urlSearch) return;
    const timer = window.setTimeout(() => updateSearchParameter(trimmed), 350);
    return () => window.clearTimeout(timer);
  }, [searchValue, updateSearchParameter, urlSearch]);

  useEffect(() => {
    if (overviewQuery.error instanceof ApiError && overviewQuery.error.status === 401) {
      queryClient.setQueryData(sessionQueryKey, null);
    }
  }, [overviewQuery.error, queryClient]);

  useEffect(() => {
    if (managedQuery.error instanceof ApiError && managedQuery.error.status === 401) {
      queryClient.setQueryData(sessionQueryKey, null);
    }
  }, [managedQuery.error, queryClient]);

  useEffect(() => {
    if (searchedQuery.error instanceof ApiError && searchedQuery.error.status === 401) {
      queryClient.setQueryData(sessionQueryKey, null);
    }
  }, [queryClient, searchedQuery.error]);

  useEffect(() => {
    if (organizing) organizeHeadingRef.current?.focus();
  }, [organizing]);

  useEffect(() => {
    if (!session) return;
    reviewQuestionRef.current?.focus();
  }, [session]);

  useEffect(() => {
    if (!completedReview) return;
    completionHeadingRef.current?.focus();
  }, [completedReview]);

  const startReview = async (collection: FlashcardReviewCollection) => {
    if (collection.ready === 0 || startingCollectionId !== undefined) return;
    setStartingCollectionId(collection.id);
    setStartError("");
    const refreshed = await overviewQuery.refetch();
    setStartingCollectionId(undefined);
    if (refreshed.error || !refreshed.data) {
      setStartError("The ready cards could not be refreshed. Try again.");
      return;
    }
    const freshCollection = refreshed.data.collections.find(({ id }) => id === collection.id);
    const snapshot = createReviewSnapshot(refreshed.data.readyCards, collection.id);
    if (!freshCollection || snapshot.length === 0) {
      setAnnouncement(`There are no cards ready in ${collection.name}.`);
      return;
    }
    launchingCollectionIdRef.current = collection.id;
    advancingRef.current = false;
    saveMutation.reset();
    setFailedSubmission(null);
    setAnswerVisible(false);
    setCompletedReview(null);
    setAnnouncement(`Review started for ${freshCollection.name}.`);
    setSession({
      collectionId: freshCollection.id,
      collectionName: freshCollection.name,
      cards: snapshot,
      index: 0,
      outcomes: emptyOutcomeCounts(),
    });
  };

  const returnToCollections = (message: string) => {
    if (saveMutation.isPending) return;
    setSession(null);
    setCompletedReview(null);
    setAnswerVisible(false);
    setArchiveConfirmationOpen(false);
    setFailedSubmission(null);
    saveMutation.reset();
    setAnnouncement(message);
    window.requestAnimationFrame(() =>
      collectionStartRefs.current.get(collectionRefKey(launchingCollectionIdRef.current))?.focus(),
    );
  };

  const submitOutcome = (
    outcome: FlashcardReviewOutcome,
    idempotencyKey: ReviewFlashcardInput["idempotencyKey"] = createUuid(),
  ) => {
    if (!session || advancingRef.current) return;
    const card = session.cards[session.index];
    if (!card) return;
    const submission = { card, outcome, idempotencyKey };
    advancingRef.current = true;
    setFailedSubmission(null);
    saveMutation.reset();
    saveMutation.mutate(submission, {
      onSuccess: (result) => {
        queryClient.setQueryData<FlashcardReviewOverview>(flashcardReviewOverviewKey, (current) =>
          current ? applySavedOutcome(current, submission, result.reviewVersion) : current,
        );
        const nextOutcomes = {
          ...session.outcomes,
          [outcome]: session.outcomes[outcome] + 1,
        };
        setAnswerVisible(false);
        setArchiveConfirmationOpen(false);
        setFailedSubmission(null);
        setAnnouncement(outcomeMessage(outcome, card.question));
        if (session.index + 1 >= session.cards.length) {
          setSession(null);
          setCompletedReview({
            collectionId: session.collectionId,
            collectionName: session.collectionName,
            total: session.cards.length,
            outcomes: nextOutcomes,
          });
        } else {
          setSession({ ...session, index: session.index + 1, outcomes: nextOutcomes });
        }
        window.requestAnimationFrame(() => {
          advancingRef.current = false;
        });
      },
      onError: (error) => {
        advancingRef.current = false;
        if (error instanceof ApiError && error.status === 401) {
          queryClient.setQueryData(sessionQueryKey, null);
          return;
        }
        setFailedSubmission(submission);
      },
    });
  };

  const overview = overviewQuery.data;
  const currentCollection = completedReview
    ? overview?.collections.find(({ id }) => id === completedReview.collectionId)
    : undefined;

  const addCollectionToOverview = (collection: FlashcardCollection) => {
    queryClient.setQueryData<FlashcardReviewOverview>(flashcardReviewOverviewKey, (current) => {
      if (!current || current.collections.some(({ id }) => id === collection.id)) return current;
      const summary = { ready: 0, scheduled: 0, archived: 0, total: 0 };
      const uncollectedIndex = current.collections.findIndex(({ id }) => id === null);
      const collections = [...current.collections];
      collections.splice(uncollectedIndex < 0 ? collections.length : uncollectedIndex, 0, {
        ...collection,
        ...summary,
      });
      return { ...current, collections };
    });
  };

  return (
    <AppShell mainClassName="flashcards-page">
      <header className="topbar flashcards-topbar">
        <div>
          <h1>Flash Cards</h1>
        </div>
      </header>

      <p className="flashcards-announcement" role="status" aria-live="polite">
        {announcement}
      </p>

      <div className="flashcards-search">
        <div className="flashcards-search-control">
          <Search size={18} aria-hidden="true" />
          <input
            id="flashcards-search-field"
            type="search"
            aria-label="Search flashcards"
            value={searchValue}
            maxLength={120}
            placeholder="Search questions, answers, or notes"
            onChange={(event) => setSearchValue(event.target.value)}
          />
          {searchValue ? (
            <button
              type="button"
              className="text-button"
              aria-label="Clear search"
              onClick={() => {
                setSearchValue("");
                updateSearchParameter("");
              }}
            >
              Clear search
            </button>
          ) : null}
        </div>
      </div>

      {overviewQuery.isPending ? (
        <FlashcardsState
          busy
          title="Loading your flashcards"
          message="Gathering your collections…"
        />
      ) : overviewQuery.isError || !overview ? (
        <FlashcardsState
          title="Flashcards could not be loaded"
          message="Your cards are still saved. Try loading them again."
          onRetry={() => void overviewQuery.refetch()}
        />
      ) : urlSearch ? (
        <SearchResultsView
          search={urlSearch}
          collections={overview.collections.filter(
            (collection): collection is FlashcardReviewCollection & { id: string } =>
              collection.id !== null,
          )}
          query={searchedQuery}
          onCollectionCreated={(collection) => {
            addCollectionToOverview(collection);
            setAnnouncement("");
          }}
          onPage={setSearchPage}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: flashcardReviewOverviewKey });
            void searchedQuery.refetch();
          }}
          onStatusChanged={(flashcard) => {
            queryClient.setQueryData<ManagedFlashcardsResponse>(
              searchedFlashcardsKey(urlSearch, searchPage),
              (current) =>
                current
                  ? {
                      ...current,
                      flashcards: current.flashcards.map((card) =>
                        card.id === flashcard.id ? flashcard : card,
                      ),
                    }
                  : current,
            );
            void queryClient.invalidateQueries({ queryKey: flashcardReviewOverviewKey });
            void queryClient.invalidateQueries({ queryKey: ["lesson-flashcards"] });
          }}
        />
      ) : organizing ? (
        <OrganizeView
          collection={organizing}
          collections={overview.collections.filter(
            (collection): collection is FlashcardReviewCollection & { id: string } =>
              collection.id !== null,
          )}
          query={managedQuery}
          headingRef={organizeHeadingRef}
          onCollectionCreated={(collection) => {
            addCollectionToOverview(collection);
            setAnnouncement("");
          }}
          onPage={setOrganizePage}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: flashcardReviewOverviewKey });
            void managedQuery.refetch();
          }}
          onStatusChanged={(flashcard) => {
            queryClient.setQueryData<ManagedFlashcardsResponse>(
              managedFlashcardsKey(organizing.id, organizePage),
              (current) =>
                current
                  ? {
                      ...current,
                      flashcards: current.flashcards.map((card) =>
                        card.id === flashcard.id ? flashcard : card,
                      ),
                    }
                  : current,
            );
            void queryClient.invalidateQueries({ queryKey: flashcardReviewOverviewKey });
            void queryClient.invalidateQueries({ queryKey: ["lesson-flashcards"] });
          }}
          onBack={() => {
            const collectionId = organizing.id;
            setOrganizing(null);
            setOrganizePage(1);
            window.requestAnimationFrame(() =>
              collectionOrganizeRefs.current.get(collectionRefKey(collectionId))?.focus(),
            );
          }}
        />
      ) : session ? (
        <ReviewView
          session={session}
          answerVisible={answerVisible}
          questionRef={reviewQuestionRef}
          archiveOutcomeRef={archiveOutcomeRef}
          pending={saveMutation.isPending}
          failed={failedSubmission !== null && !archiveConfirmationOpen}
          onRetry={() => {
            if (failedSubmission)
              submitOutcome(failedSubmission.outcome, failedSubmission.idempotencyKey);
          }}
          onToggleAnswer={() => setAnswerVisible((visible) => !visible)}
          onOutcome={submitOutcome}
          onArchive={() => {
            saveMutation.reset();
            setFailedSubmission(null);
            setArchiveConfirmationOpen(true);
          }}
          onExit={() => returnToCollections("Review ended. Saved choices have been kept.")}
        />
      ) : completedReview ? (
        <CompletionView
          review={completedReview}
          headingRef={completionHeadingRef}
          readyCount={currentCollection?.ready ?? 0}
          onReviewAgain={() => {
            if (currentCollection) void startReview(currentCollection);
          }}
          onBack={() => returnToCollections("Review summary closed.")}
        />
      ) : (
        <CollectionSelection
          overview={overview}
          startError={startError}
          startingCollectionId={startingCollectionId}
          newCollectionButtonRef={newCollectionButtonRef}
          newFlashcardButtonRef={newFlashcardButtonRef}
          onNewCollection={() => setNewCollectionOpen(true)}
          onNewFlashcard={() => setNewFlashcardOpen(true)}
          onStartButtonRef={(collectionId, button) => {
            const key = collectionRefKey(collectionId);
            if (button) collectionStartRefs.current.set(key, button);
            else collectionStartRefs.current.delete(key);
          }}
          onOrganizeButtonRef={(collectionId, button) => {
            const key = collectionRefKey(collectionId);
            if (button) collectionOrganizeRefs.current.set(key, button);
            else collectionOrganizeRefs.current.delete(key);
          }}
          onOrganize={(collection) => {
            setOrganizePage(1);
            setOrganizing(collection);
          }}
          onStart={(collection) => void startReview(collection)}
        />
      )}

      {newCollectionOpen ? (
        <NewFlashcardCollectionDialog
          onCreated={(collection) => {
            addCollectionToOverview(collection);
            setNewCollectionOpen(false);
            setAnnouncement("");
            window.requestAnimationFrame(() => newCollectionButtonRef.current?.focus());
          }}
          onDismiss={() => {
            setNewCollectionOpen(false);
            window.requestAnimationFrame(() => newCollectionButtonRef.current?.focus());
          }}
        />
      ) : null}

      {newFlashcardOpen && overview ? (
        <FlashcardDialog
          activityId={null}
          initialQuestion=""
          collections={overview.collections.filter(
            (collection): collection is FlashcardReviewCollection & { id: string } =>
              collection.id !== null,
          )}
          onCollectionCreated={(collection) => {
            addCollectionToOverview(collection);
            setAnnouncement(`Created and selected ${collection.name}.`);
          }}
          onSaved={(result) => {
            setNewFlashcardOpen(false);
            setAnnouncement(
              result.flashcards.length === 2 ? "Two flashcards created." : "Flashcard created.",
            );
            void queryClient.invalidateQueries({ queryKey: flashcardReviewOverviewKey });
            void queryClient.invalidateQueries({ queryKey: ["managed-flashcards"] });
            void queryClient.invalidateQueries({ queryKey: ["searched-flashcards"] });
            window.requestAnimationFrame(() => newFlashcardButtonRef.current?.focus());
          }}
          onUnauthorized={() => queryClient.setQueryData(sessionQueryKey, null)}
          onClose={() => {
            setNewFlashcardOpen(false);
            window.requestAnimationFrame(() => newFlashcardButtonRef.current?.focus());
          }}
        />
      ) : null}

      {archiveConfirmationOpen && session ? (
        <ArchiveConfirmationDialog
          card={session.cards[session.index]}
          pending={saveMutation.isPending}
          failed={failedSubmission?.outcome === "archive"}
          onCancel={() => {
            if (saveMutation.isPending) return;
            setArchiveConfirmationOpen(false);
            setFailedSubmission(null);
            saveMutation.reset();
            window.requestAnimationFrame(() => archiveOutcomeRef.current?.focus());
          }}
          onConfirm={() => {
            if (failedSubmission?.outcome === "archive") {
              submitOutcome("archive", failedSubmission.idempotencyKey);
            } else {
              submitOutcome("archive");
            }
          }}
        />
      ) : null}
    </AppShell>
  );
}

function FlashcardsState({
  busy = false,
  title,
  message,
  onRetry,
}: {
  busy?: boolean;
  title: string;
  message: string;
  onRetry?: () => void;
}): React.JSX.Element {
  return (
    <section className="flashcards-state" aria-busy={busy} {...(!busy ? { role: "alert" } : {})}>
      <h2>{title}</h2>
      <p>{message}</p>
      {onRetry ? (
        <button type="button" className="soft-button" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </section>
  );
}

function CollectionSelection({
  overview,
  startError,
  startingCollectionId,
  newCollectionButtonRef,
  newFlashcardButtonRef,
  onNewCollection,
  onNewFlashcard,
  onStartButtonRef,
  onOrganizeButtonRef,
  onOrganize,
  onStart,
}: {
  overview: FlashcardReviewOverview;
  startError: string;
  startingCollectionId: string | null | undefined;
  newCollectionButtonRef: React.RefObject<HTMLButtonElement | null>;
  newFlashcardButtonRef: React.RefObject<HTMLButtonElement | null>;
  onNewCollection: () => void;
  onNewFlashcard: () => void;
  onStartButtonRef: (collectionId: string | null, button: HTMLButtonElement | null) => void;
  onOrganizeButtonRef: (collectionId: string | null, button: HTMLButtonElement | null) => void;
  onOrganize: (collection: FlashcardReviewCollection) => void;
  onStart: (collection: FlashcardReviewCollection) => void;
}): React.JSX.Element {
  return (
    <section className="flashcards-selection" aria-labelledby="flashcards-selection-title">
      <div className="flashcards-selection-intro">
        <div>
          <h2 id="flashcards-selection-title">What would you like to review?</h2>
        </div>
        <div className="flashcards-create-actions">
          <button
            ref={newFlashcardButtonRef}
            type="button"
            className="soft-button flashcards-new-flashcard-button"
            onClick={onNewFlashcard}
          >
            <Plus size={15} aria-hidden="true" /> New flashcard
          </button>
          <button
            ref={newCollectionButtonRef}
            type="button"
            className="soft-button flashcards-new-collection-button"
            onClick={onNewCollection}
          >
            <Plus size={15} aria-hidden="true" /> New collection
          </button>
        </div>
      </div>
      {overview.summary.total === 0 ? (
        <p className="flashcards-empty-copy">Create a flashcard to begin reviewing.</p>
      ) : null}
      {startError ? (
        <p className="field-error flashcards-start-error" role="alert">
          {startError}
        </p>
      ) : null}
      <div className="flashcards-collection-grid">
        {overview.collections.map((collection) => (
          <article key={collection.id ?? "uncollected"} className="flashcards-collection-card">
            <span className="flashcards-collection-icon" aria-hidden="true">
              <Layers3 size={19} />
            </span>
            <span className="flashcards-collection-copy">
              <strong>{collection.name}</strong>
              {collection.id === null ? (
                <span>Cards that have not been added to a collection</span>
              ) : null}
            </span>
            <span className="flashcards-collection-count">
              <strong>{collection.ready}</strong>
              <span>ready</span>
            </span>
            <span className="flashcards-collection-footer">
              <CollectionStatus collection={collection} />
              <span className="flashcards-collection-actions">
                <button
                  ref={(button) => onOrganizeButtonRef(collection.id, button)}
                  type="button"
                  className="primary-button flashcards-collection-organize"
                  aria-label={`Organize ${collection.name}`}
                  onClick={() => onOrganize(collection)}
                >
                  <Pencil size={16} aria-hidden="true" />
                </button>
                <button
                  ref={(button) => onStartButtonRef(collection.id, button)}
                  type="button"
                  className="primary-button flashcards-collection-start"
                  aria-label={`Start ${collection.name} review`}
                  aria-disabled={collection.ready === 0}
                  disabled={startingCollectionId !== undefined}
                  onClick={() => {
                    if (collection.ready > 0) onStart(collection);
                  }}
                >
                  {startingCollectionId === collection.id ? "Starting…" : "Start review"}
                </button>
              </span>
            </span>
          </article>
        ))}
      </div>
    </section>
  );
}

function SearchResultsView({
  search,
  collections,
  query,
  onCollectionCreated,
  onPage,
  onChanged,
  onStatusChanged,
}: {
  search: string;
  collections: FlashcardCollection[];
  query: UseQueryResult<ManagedFlashcardsResponse, Error>;
  onCollectionCreated: (collection: FlashcardCollection) => void;
  onPage: (page: number) => void;
  onChanged: () => void;
  onStatusChanged: (card: Flashcard) => void;
}): React.JSX.Element {
  const [editTarget, setEditTarget] = useState<{
    card: Flashcard;
    trigger: HTMLButtonElement;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    card: Flashcard;
    trigger: HTMLButtonElement;
  } | null>(null);
  const data = query.data;
  const first = data && data.total > 0 ? (data.page - 1) * data.pageSize + 1 : 0;
  const last = data ? Math.min(data.total, data.page * data.pageSize) : 0;

  const closeEdit = () => {
    const trigger = editTarget?.trigger;
    setEditTarget(null);
    window.requestAnimationFrame(() => trigger?.focus());
  };
  const closeDelete = () => {
    const trigger = deleteTarget?.trigger;
    setDeleteTarget(null);
    window.requestAnimationFrame(() => trigger?.focus());
  };

  return (
    <section
      className="flashcards-organize flashcards-search-results"
      aria-labelledby="flashcards-search-title"
    >
      <div className="flashcards-organize-heading">
        <div>
          <span className="section-kicker">Search results</span>
          <h2 id="flashcards-search-title">Flashcards matching “{search}”</h2>
          {data ? (
            <p role="status">
              {data.total === 0
                ? "No matching cards"
                : `Showing ${String(first)}–${String(last)} of ${String(data.total)} ${data.total === 1 ? "card" : "cards"}`}
            </p>
          ) : null}
        </div>
      </div>

      {query.isPending ? (
        <FlashcardsState busy title="Searching flashcards" message="Looking through your cards…" />
      ) : query.isError || !data ? (
        <FlashcardsState
          title="Search could not be completed"
          message="Your cards are still saved and your search is still here. Try again."
          onRetry={() => void query.refetch()}
        />
      ) : data.total === 0 ? (
        <div className="flashcards-organize-empty">
          <h3>No flashcards match “{search}”</h3>
          <p>Try another question, answer, or note.</p>
        </div>
      ) : (
        <>
          <ol className="flashcards-organize-list" aria-label={`Flashcards matching ${search}`}>
            {data.flashcards.map((card) => (
              <ManagedFlashcardRow
                key={card.id}
                card={card}
                today={data.today}
                onStatusChanged={onStatusChanged}
                onEdit={(trigger) => setEditTarget({ card, trigger })}
                onDelete={(trigger) => setDeleteTarget({ card, trigger })}
              />
            ))}
          </ol>
          <nav className="flashcards-pagination" aria-label="Search result pages">
            <button
              type="button"
              className="soft-button"
              disabled={data.page <= 1}
              onClick={() => onPage(data.page - 1)}
            >
              <ChevronLeft size={16} aria-hidden="true" /> Previous
            </button>
            <span>
              Page {data.page} of {data.pageCount}
            </span>
            <button
              type="button"
              className="soft-button"
              disabled={data.page >= data.pageCount}
              onClick={() => onPage(data.page + 1)}
            >
              Next <ChevronRight size={16} aria-hidden="true" />
            </button>
          </nav>
        </>
      )}

      {editTarget ? (
        <EditFlashcardDialog
          card={editTarget.card}
          collections={collections}
          onCollectionCreated={onCollectionCreated}
          onSaved={() => {
            closeEdit();
            onChanged();
          }}
          onDismiss={closeEdit}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteManagedFlashcardDialog
          card={deleteTarget.card}
          onDeleted={() => {
            closeDelete();
            if (data?.flashcards.length === 1 && data.page > 1) onPage(data.page - 1);
            onChanged();
          }}
          onDismiss={closeDelete}
        />
      ) : null}
    </section>
  );
}

function OrganizeView({
  collection,
  collections,
  query,
  headingRef,
  onCollectionCreated,
  onPage,
  onChanged,
  onStatusChanged,
  onBack,
}: {
  collection: FlashcardReviewCollection;
  collections: FlashcardCollection[];
  query: UseQueryResult<ManagedFlashcardsResponse, Error>;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  onCollectionCreated: (collection: FlashcardCollection) => void;
  onPage: (page: number) => void;
  onChanged: () => void;
  onStatusChanged: (card: Flashcard) => void;
  onBack: () => void;
}): React.JSX.Element {
  const [editTarget, setEditTarget] = useState<{
    card: Flashcard;
    trigger: HTMLButtonElement;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    card: Flashcard;
    trigger: HTMLButtonElement;
  } | null>(null);
  const data = query.data;
  const first = data && data.total > 0 ? (data.page - 1) * data.pageSize + 1 : 0;
  const last = data ? Math.min(data.total, data.page * data.pageSize) : 0;

  const closeEdit = () => {
    const trigger = editTarget?.trigger;
    setEditTarget(null);
    window.requestAnimationFrame(() => trigger?.focus());
  };
  const closeDelete = () => {
    const trigger = deleteTarget?.trigger;
    setDeleteTarget(null);
    window.requestAnimationFrame(() => trigger?.focus());
  };

  return (
    <section className="flashcards-organize" aria-labelledby="flashcards-organize-title">
      <div className="flashcards-organize-heading">
        <div>
          <span className="section-kicker">Organize collection</span>
          <h2 id="flashcards-organize-title" ref={headingRef} tabIndex={-1}>
            {collection.name}
          </h2>
          {data ? (
            <p>
              Showing {first}–{last} of {data.total} {data.total === 1 ? "card" : "cards"}
            </p>
          ) : null}
        </div>
        <button type="button" className="soft-button" onClick={onBack}>
          <ChevronLeft size={16} aria-hidden="true" /> Back to collections
        </button>
      </div>

      {query.isPending ? (
        <FlashcardsState busy title="Loading this collection" message="Gathering every card…" />
      ) : query.isError || !data ? (
        <FlashcardsState
          title="This collection could not be loaded"
          message="Your cards are still saved. Try loading them again."
          onRetry={() => void query.refetch()}
        />
      ) : data.total === 0 ? (
        <div className="flashcards-organize-empty">
          <h3>No flashcards in this collection</h3>
          <p>Move a flashcard here or choose another collection.</p>
        </div>
      ) : (
        <>
          <ol className="flashcards-organize-list" aria-label={`${collection.name} flashcards`}>
            {data.flashcards.map((card) => (
              <ManagedFlashcardRow
                key={card.id}
                card={card}
                today={data.today}
                onStatusChanged={onStatusChanged}
                onEdit={(trigger) => setEditTarget({ card, trigger })}
                onDelete={(trigger) => setDeleteTarget({ card, trigger })}
              />
            ))}
          </ol>
          <nav className="flashcards-pagination" aria-label="Flashcard pages">
            <button
              type="button"
              className="soft-button"
              disabled={data.page <= 1}
              onClick={() => onPage(data.page - 1)}
            >
              <ChevronLeft size={16} aria-hidden="true" /> Previous
            </button>
            <span>
              Page {data.page} of {data.pageCount}
            </span>
            <button
              type="button"
              className="soft-button"
              disabled={data.page >= data.pageCount}
              onClick={() => onPage(data.page + 1)}
            >
              Next <ChevronRight size={16} aria-hidden="true" />
            </button>
          </nav>
        </>
      )}

      {editTarget ? (
        <EditFlashcardDialog
          card={editTarget.card}
          collections={collections}
          onCollectionCreated={onCollectionCreated}
          onSaved={() => {
            closeEdit();
            onChanged();
          }}
          onDismiss={closeEdit}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteManagedFlashcardDialog
          card={deleteTarget.card}
          onDeleted={() => {
            closeDelete();
            if (data?.flashcards.length === 1 && data.page > 1) onPage(data.page - 1);
            onChanged();
          }}
          onDismiss={closeDelete}
        />
      ) : null}
    </section>
  );
}

function ManagedFlashcardRow({
  card,
  today,
  onStatusChanged,
  onEdit,
  onDelete,
}: {
  card: Flashcard;
  today: string;
  onStatusChanged: (card: Flashcard) => void;
  onEdit: (trigger: HTMLButtonElement) => void;
  onDelete: (trigger: HTMLButtonElement) => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const pendingRef = useRef(false);
  const [pendingAction, setPendingAction] = useState<ManagedFlashcardStatusAction | null>(null);
  const [failedAction, setFailedAction] = useState<ManagedFlashcardStatusAction | null>(null);
  const isFuture = card.archivedAt === null && card.reviewDate !== null && card.reviewDate > today;
  const actions: ManagedFlashcardStatusAction[] = card.archivedAt
    ? ["unarchive"]
    : isFuture
      ? ["archive", "bring-current"]
      : ["archive"];

  const changeStatus = async (action: ManagedFlashcardStatusAction) => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPendingAction(action);
    setFailedAction(null);
    try {
      const result = await updateManagedFlashcardStatus(card.id, {
        action,
        expectedVersion: card.reviewVersion,
      });
      onStatusChanged(result.flashcard);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        queryClient.setQueryData(sessionQueryKey, null);
        return;
      }
      setFailedAction(action);
    } finally {
      pendingRef.current = false;
      setPendingAction(null);
    }
  };

  return (
    <li className="flashcards-organize-card">
      <div className="flashcards-organize-preview">
        <div>
          <span className="flashcard-side-label">Question</span>
          <h3 title={card.question}>{card.question}</h3>
        </div>
        <div>
          <span className="flashcard-side-label">Answer</span>
          <p title={card.answer}>{card.answer}</p>
        </div>
        <span className="flashcards-organize-state">
          {card.archivedAt ? "Archived" : isFuture ? `Scheduled ${card.reviewDate}` : "Ready"}
        </span>
      </div>
      <div className="flashcards-organize-actions">
        {actions.map((action) => (
          <button
            key={action}
            type="button"
            className="icon-button flashcards-organize-status-action"
            aria-label={`${statusActionLabel(action)} ${card.question}`}
            title={statusActionLabel(action)}
            disabled={pendingAction !== null}
            onClick={() => void changeStatus(action)}
          >
            {action === "archive" ? (
              <ArchiveIcon size={17} aria-hidden="true" />
            ) : action === "unarchive" ? (
              <RotateCcw size={17} aria-hidden="true" />
            ) : (
              <CalendarClock size={17} aria-hidden="true" />
            )}
          </button>
        ))}
        <button
          type="button"
          className="icon-button"
          aria-label={`Edit ${card.question}`}
          disabled={pendingAction !== null}
          onClick={(event) => onEdit(event.currentTarget)}
        >
          <Pencil size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="icon-button flashcards-organize-delete"
          aria-label={`Delete ${card.question}`}
          disabled={pendingAction !== null}
          onClick={(event) => onDelete(event.currentTarget)}
        >
          <Trash2 size={16} aria-hidden="true" />
        </button>
      </div>
      <span className="flashcards-organize-action-status" role={failedAction ? "alert" : "status"}>
        {pendingAction
          ? `${statusActionProgress(pendingAction)}…`
          : failedAction
            ? `${statusActionLabel(failedAction)} failed. Try again.`
            : ""}
      </span>
    </li>
  );
}

function statusActionLabel(action: ManagedFlashcardStatusAction): string {
  if (action === "archive") return "Archive";
  if (action === "unarchive") return "Unarchive";
  return "Bring current";
}

function statusActionProgress(action: ManagedFlashcardStatusAction): string {
  if (action === "archive") return "Archiving";
  if (action === "unarchive") return "Unarchiving";
  return "Bringing current";
}

function EditFlashcardDialog({
  card,
  collections,
  onCollectionCreated,
  onSaved,
  onDismiss,
}: {
  card: Flashcard;
  collections: FlashcardCollection[];
  onCollectionCreated: (collection: FlashcardCollection) => void;
  onSaved: () => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const questionRef = useRef<HTMLTextAreaElement>(null);
  const submittingRef = useRef(false);
  const headingId = useId();
  const collectionIdField = useId();
  const newCollectionId = useId();
  const newCollectionErrorId = useId();
  const newCollectionRef = useRef<HTMLInputElement>(null);
  const [question, setQuestion] = useState(card.question);
  const [answer, setAnswer] = useState(card.answer);
  const [notes, setNotes] = useState(card.notes);
  const [collectionId, setCollectionId] = useState(card.collectionId ?? "");
  const [errors, setErrors] = useState<{
    question?: string;
    answer?: string;
    collectionId?: string;
  }>({});
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [collectionCreateError, setCollectionCreateError] = useState("");
  const [creatingCollection, setCreatingCollection] = useState(false);
  const creatingCollectionRef = useRef(false);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    questionRef.current?.focus();
    return () => dialog.close();
  }, []);

  useEffect(() => {
    if (showNewCollection) newCollectionRef.current?.focus();
  }, [showNewCollection]);

  const createCollection = async () => {
    if (creatingCollectionRef.current) return;
    const parsed = createFlashcardCollectionInputSchema.safeParse({ name: newCollectionName });
    if (!parsed.success) {
      setCollectionCreateError(
        parsed.error.flatten().fieldErrors.name?.[0] ?? "Enter a collection name.",
      );
      newCollectionRef.current?.focus();
      return;
    }
    creatingCollectionRef.current = true;
    setCreatingCollection(true);
    setCollectionCreateError("");
    try {
      const result = await createFlashcardCollection(parsed.data);
      onCollectionCreated(result.collection);
      setCollectionId(result.collection.id);
      setErrors((current) => removeOrganizeError(current, "collectionId"));
      setNewCollectionName("");
      setShowNewCollection(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        queryClient.setQueryData(sessionQueryKey, null);
        return;
      }
      const fieldError =
        error instanceof ApiError ? error.problem?.fieldErrors?.name?.[0] : undefined;
      setCollectionCreateError(
        fieldError ??
          (error instanceof ApiError && error.problem?.detail
            ? error.problem.detail
            : "The collection could not be created. Your name is still here; try again."),
      );
    } finally {
      creatingCollectionRef.current = false;
      setCreatingCollection(false);
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current) return;
    const nextErrors: typeof errors = {};
    if (!question.trim()) nextErrors.question = "Enter text for this side of the flashcard.";
    else if (question.trim().length > 10_000)
      nextErrors.question = "Keep each side of the flashcard under 10,000 characters.";
    if (!answer.trim()) nextErrors.answer = "Enter text for this side of the flashcard.";
    else if (answer.trim().length > 10_000)
      nextErrors.answer = "Keep each side of the flashcard under 10,000 characters.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    submittingRef.current = true;
    setSaving(true);
    setSaveError("");
    try {
      await updateManagedFlashcard(card.id, {
        question,
        answer,
        notes,
        collectionId: collectionId || null,
        expectedVersion: card.reviewVersion,
      });
      onSaved();
    } catch (error) {
      submittingRef.current = false;
      setSaving(false);
      if (error instanceof ApiError && error.status === 401) {
        queryClient.setQueryData(sessionQueryKey, null);
        return;
      }
      const fieldErrors = error instanceof ApiError ? error.problem?.fieldErrors : undefined;
      setErrors({
        ...(fieldErrors?.question?.[0] ? { question: fieldErrors.question[0] } : {}),
        ...(fieldErrors?.answer?.[0] ? { answer: fieldErrors.answer[0] } : {}),
        ...(fieldErrors?.collectionId?.[0] ? { collectionId: fieldErrors.collectionId[0] } : {}),
      });
      setSaveError(
        error instanceof ApiError && error.problem?.detail
          ? error.problem.detail
          : "The flashcard was not saved. Your entries are still here; try again.",
      );
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="flashcard-dialog"
      aria-labelledby={headingId}
      onCancel={(event) => {
        event.preventDefault();
        if (!saving) onDismiss();
      }}
    >
      <div className="flashcard-dialog-heading">
        <div>
          <span className="section-kicker">Organize flashcard</span>
          <h2 id={headingId}>Edit flashcard</h2>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="Close edit form"
          disabled={saving || creatingCollection}
          onClick={onDismiss}
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <form onSubmit={(event) => void submit(event)} noValidate>
        <label className="flashcard-field">
          <span>Question</span>
          <textarea
            ref={questionRef}
            value={question}
            rows={4}
            aria-invalid={Boolean(errors.question)}
            onChange={(event) => {
              setQuestion(event.target.value);
              setErrors((current) => removeOrganizeError(current, "question"));
            }}
          />
          {errors.question ? (
            <span className="field-error" role="alert">
              {errors.question}
            </span>
          ) : null}
        </label>
        <label className="flashcard-field">
          <span>Answer</span>
          <textarea
            value={answer}
            rows={4}
            aria-invalid={Boolean(errors.answer)}
            onChange={(event) => {
              setAnswer(event.target.value);
              setErrors((current) => removeOrganizeError(current, "answer"));
            }}
          />
          {errors.answer ? (
            <span className="field-error" role="alert">
              {errors.answer}
            </span>
          ) : null}
        </label>
        <label className="flashcard-field">
          <span>Notes</span>
          <textarea value={notes} rows={5} onChange={(event) => setNotes(event.target.value)} />
        </label>
        <div className="flashcard-collection-row">
          <div className="flashcard-field">
            <label htmlFor={collectionIdField}>Flashcard collection</label>
            <select
              id={collectionIdField}
              value={collectionId}
              aria-invalid={Boolean(errors.collectionId)}
              onChange={(event) => {
                setCollectionId(event.target.value);
                setErrors((current) => removeOrganizeError(current, "collectionId"));
              }}
            >
              <option value="">Uncollected</option>
              {collections.map((collection) => (
                <option key={collection.id} value={collection.id}>
                  {collection.name}
                </option>
              ))}
            </select>
            {errors.collectionId ? (
              <span className="field-error" role="alert">
                {errors.collectionId}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="text-button"
            aria-expanded={showNewCollection}
            disabled={saving || creatingCollection}
            onClick={() => {
              setShowNewCollection((current) => !current);
              setCollectionCreateError("");
            }}
          >
            <Plus size={14} aria-hidden="true" /> New collection
          </button>
        </div>
        {showNewCollection ? (
          <>
            <div className="flashcard-new-collection">
              <div className="flashcard-field">
                <label htmlFor={newCollectionId}>New collection name</label>
                <input
                  ref={newCollectionRef}
                  id={newCollectionId}
                  value={newCollectionName}
                  aria-invalid={Boolean(collectionCreateError)}
                  aria-describedby={collectionCreateError ? newCollectionErrorId : undefined}
                  disabled={creatingCollection}
                  onChange={(event) => {
                    setNewCollectionName(event.target.value);
                    setCollectionCreateError("");
                  }}
                />
              </div>
              <button
                type="button"
                className="soft-button"
                disabled={creatingCollection}
                onClick={() => void createCollection()}
              >
                {creatingCollection ? "Creating…" : "Create collection"}
              </button>
              {collectionCreateError ? (
                <p id={newCollectionErrorId} className="field-error" role="alert">
                  {collectionCreateError}
                </p>
              ) : null}
            </div>
            <p
              className="flashcard-create-progress"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {creatingCollection ? "Creating collection…" : ""}
            </p>
          </>
        ) : null}
        {saveError ? (
          <p className="mutation-error" role="alert">
            {saveError}
          </p>
        ) : null}
        <div className="flashcard-dialog-actions">
          <button
            type="button"
            className="soft-button"
            disabled={saving || creatingCollection}
            onClick={onDismiss}
          >
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={saving || creatingCollection}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function NewFlashcardCollectionDialog({
  onCreated,
  onDismiss,
}: {
  onCreated: (collection: FlashcardCollection) => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const pendingRef = useRef(false);
  const headingId = useId();
  const nameId = useId();
  const errorId = useId();
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    nameRef.current?.focus();
    return () => dialog.close();
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pendingRef.current) return;
    const parsed = createFlashcardCollectionInputSchema.safeParse({ name });
    if (!parsed.success) {
      setError(parsed.error.flatten().fieldErrors.name?.[0] ?? "Enter a collection name.");
      nameRef.current?.focus();
      return;
    }
    pendingRef.current = true;
    setPending(true);
    setError("");
    try {
      const result = await createFlashcardCollection(parsed.data);
      onCreated(result.collection);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        queryClient.setQueryData(sessionQueryKey, null);
        return;
      }
      const fieldError =
        caught instanceof ApiError ? caught.problem?.fieldErrors?.name?.[0] : undefined;
      setError(
        fieldError ??
          (caught instanceof ApiError && caught.problem?.detail
            ? caught.problem.detail
            : "The collection could not be created. Your name is still here; try again."),
      );
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="flashcard-dialog flashcard-collection-dialog"
      aria-labelledby={headingId}
      onCancel={(event) => {
        event.preventDefault();
        if (!pending) onDismiss();
      }}
    >
      <div className="flashcard-dialog-heading">
        <div>
          <span className="section-kicker">Flash Cards</span>
          <h2 id={headingId}>New collection</h2>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="Close new collection form"
          disabled={pending}
          onClick={onDismiss}
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <form onSubmit={(event) => void submit(event)} noValidate>
        <div className="flashcard-field">
          <label htmlFor={nameId}>Collection name</label>
          <input
            ref={nameRef}
            id={nameId}
            value={name}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            disabled={pending}
            onChange={(event) => {
              setName(event.target.value);
              setError("");
            }}
          />
          {error ? (
            <span id={errorId} className="field-error" role="alert">
              {error}
            </span>
          ) : null}
        </div>
        <p className="flashcard-create-progress" role="status" aria-live="polite">
          {pending ? "Creating collection…" : ""}
        </p>
        <div className="flashcard-dialog-actions">
          <button type="button" className="soft-button" disabled={pending} onClick={onDismiss}>
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={pending}>
            {pending ? "Creating…" : "Create collection"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function DeleteManagedFlashcardDialog({
  card,
  onDeleted,
  onDismiss,
}: {
  card: Flashcard;
  onDeleted: () => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    cancelRef.current?.focus();
    return () => dialog.close();
  }, []);
  const remove = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setFailed(false);
    try {
      await deleteManagedFlashcard(card.id, { expectedVersion: card.reviewVersion });
      onDeleted();
    } catch (error) {
      pendingRef.current = false;
      setPending(false);
      if (error instanceof ApiError && error.status === 401) {
        queryClient.setQueryData(sessionQueryKey, null);
        return;
      }
      setFailed(true);
    }
  };
  return (
    <dialog
      ref={dialogRef}
      className="calm-dialog delete-flashcard-dialog"
      aria-labelledby="delete-managed-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!pending) onDismiss();
      }}
    >
      <div>
        <span className="section-kicker">Delete flashcard</span>
        <h2 id="delete-managed-title">Delete this flashcard?</h2>
        <p className="delete-flashcard-question">{card.question}</p>
        <p>This flashcard will be permanently deleted. This cannot be undone.</p>
        {failed ? (
          <p className="field-error" role="alert">
            The flashcard could not be deleted. It is still here; try again.
          </p>
        ) : null}
        <div className="dialog-actions">
          <button
            ref={cancelRef}
            type="button"
            className="soft-button"
            disabled={pending}
            onClick={onDismiss}
          >
            Keep flashcard
          </button>
          <button
            type="button"
            className="danger-button"
            disabled={pending}
            onClick={() => void remove()}
          >
            {pending ? "Deleting…" : failed ? "Try deleting again" : "Delete flashcard"}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function ReviewView({
  session,
  answerVisible,
  questionRef,
  archiveOutcomeRef,
  pending,
  failed,
  onRetry,
  onToggleAnswer,
  onOutcome,
  onArchive,
  onExit,
}: {
  session: ReviewSession;
  answerVisible: boolean;
  questionRef: React.RefObject<HTMLHeadingElement | null>;
  archiveOutcomeRef: React.RefObject<HTMLButtonElement | null>;
  pending: boolean;
  failed: boolean;
  onRetry: () => void;
  onToggleAnswer: () => void;
  onOutcome: (outcome: FlashcardReviewOutcome) => void;
  onArchive: () => void;
  onExit: () => void;
}): React.JSX.Element {
  const card = session.cards[session.index];
  if (!card) return <></>;
  return (
    <section className="flashcards-review" aria-labelledby="flashcards-review-question">
      <div className="flashcards-review-heading">
        <div>
          <span className="section-kicker">Reviewing {session.collectionName}</span>
          <p>
            Card {session.index + 1} of {session.cards.length}
          </p>
        </div>
        <button type="button" className="soft-button" disabled={pending} onClick={onExit}>
          <X size={16} aria-hidden="true" /> Exit review
        </button>
      </div>
      <progress
        className="flashcards-progress"
        value={session.index + 1}
        max={session.cards.length}
        aria-label={`Card ${String(session.index + 1)} of ${String(session.cards.length)}`}
      />
      <article className="flashcards-review-card">
        <span className="flashcard-side-label">Question</span>
        <h2 id="flashcards-review-question" ref={questionRef} tabIndex={-1}>
          {card.question}
        </h2>
        {answerVisible ? (
          <div className="flashcards-review-answer">
            <span className="flashcard-side-label">Answer</span>
            <p>{card.answer}</p>
            {card.notes ? (
              <div className="flashcards-review-notes">
                <span className="flashcard-side-label">Notes</span>
                <p>{card.notes}</p>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="flashcards-review-prompt">Take a moment to recall the answer first.</p>
        )}
        <button
          type="button"
          className="soft-button flashcards-reveal-button"
          aria-expanded={answerVisible}
          disabled={pending}
          onClick={onToggleAnswer}
        >
          {answerVisible ? (
            <>
              <EyeOff size={16} aria-hidden="true" /> Hide answer
            </>
          ) : (
            <>
              <Eye size={16} aria-hidden="true" /> Reveal answer
            </>
          )}
        </button>
      </article>
      {answerVisible ? (
        <div
          className="flashcards-outcomes"
          aria-labelledby="flashcards-outcomes-title"
          aria-busy={pending}
        >
          <div>
            <span className="section-kicker">Choose what happens next</span>
            <h2 id="flashcards-outcomes-title">When should this card return?</h2>
          </div>
          {failed ? (
            <div className="flashcards-save-error" role="alert">
              <span>The choice was not saved. This card is still here.</span>
              <button type="button" className="text-button" onClick={onRetry}>
                Try again
              </button>
            </div>
          ) : null}
          <div className="flashcards-outcome-grid">
            <OutcomeButton
              label="Keep current"
              detail="Leave it ready for your next review"
              icon={<RotateCcw size={18} aria-hidden="true" />}
              disabled={pending}
              onClick={() => onOutcome("keep-current")}
            />
            <OutcomeButton
              label="Review in one week"
              detail="Pause it for one week"
              icon={<CalendarClock size={18} aria-hidden="true" />}
              disabled={pending}
              onClick={() => onOutcome("one-week")}
            />
            <OutcomeButton
              label="Review in one month"
              detail="Pause it for one month"
              icon={<CalendarClock size={18} aria-hidden="true" />}
              disabled={pending}
              onClick={() => onOutcome("one-month")}
            />
            <OutcomeButton
              label="Review in three months"
              detail="Pause it for three months"
              icon={<CalendarClock size={18} aria-hidden="true" />}
              disabled={pending}
              onClick={() => onOutcome("three-months")}
            />
            <button
              ref={archiveOutcomeRef}
              type="button"
              className="flashcards-outcome-button archive"
              aria-label="Permanently archive"
              disabled={pending}
              onClick={onArchive}
            >
              <ArchiveIcon size={18} aria-hidden="true" />
              <span>
                <strong>Permanently archive</strong>
                <small>Remove it from active reviews</small>
              </span>
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function OutcomeButton({
  label,
  detail,
  icon,
  disabled,
  onClick,
}: {
  label: string;
  detail: string;
  icon: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="flashcards-outcome-button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
      <span>
        <strong>{label}</strong>
        <small>{detail}</small>
      </span>
    </button>
  );
}

function CompletionView({
  review,
  headingRef,
  readyCount,
  onReviewAgain,
  onBack,
}: {
  review: CompletedReview;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  readyCount: number;
  onReviewAgain: () => void;
  onBack: () => void;
}): React.JSX.Element {
  return (
    <section className="flashcards-complete" aria-labelledby="flashcards-complete-title">
      <span className="flashcards-complete-icon" aria-hidden="true">
        <Check size={28} />
      </span>
      <span className="section-kicker">Session complete</span>
      <h2 id="flashcards-complete-title" ref={headingRef} tabIndex={-1}>
        Review complete
      </h2>
      <p>
        You reviewed {review.total} {review.total === 1 ? "card" : "cards"} from{" "}
        {review.collectionName}.
      </p>
      <dl className="flashcards-complete-summary">
        <SummaryItem label="Kept current" value={review.outcomes["keep-current"]} />
        <SummaryItem label="In one week" value={review.outcomes["one-week"]} />
        <SummaryItem label="In one month" value={review.outcomes["one-month"]} />
        <SummaryItem label="In three months" value={review.outcomes["three-months"]} />
        <SummaryItem label="Archived" value={review.outcomes.archive} archive />
      </dl>
      <div className="flashcards-complete-actions">
        <button type="button" className="soft-button" onClick={onBack}>
          Back to collections
        </button>
        <button
          type="button"
          className="primary-button"
          aria-disabled={readyCount === 0}
          onClick={() => {
            if (readyCount > 0) onReviewAgain();
          }}
        >
          <RotateCcw size={16} aria-hidden="true" /> Review ready cards ({readyCount})
        </button>
      </div>
    </section>
  );
}

function SummaryItem({
  label,
  value,
  archive = false,
}: {
  label: string;
  value: number;
  archive?: boolean;
}): React.JSX.Element {
  return (
    <div className={archive ? "archive" : undefined}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function ArchiveConfirmationDialog({
  card,
  pending,
  failed,
  onCancel,
  onConfirm,
}: {
  card: FlashcardReviewCard | undefined;
  pending: boolean;
  failed: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    cancelRef.current?.focus();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);
  if (!card) return <></>;
  return (
    <dialog
      ref={dialogRef}
      className="calm-dialog flashcards-archive-dialog"
      aria-labelledby="flashcards-archive-title"
      aria-describedby="flashcards-archive-description"
      onCancel={(event) => {
        event.preventDefault();
        if (!pending) onCancel();
      }}
    >
      <div>
        <span className="section-kicker">Archive card</span>
        <h2 id="flashcards-archive-title">Archive this card permanently?</h2>
        <p className="flashcards-archive-question">{card.question}</p>
        <p id="flashcards-archive-description">
          It will be removed from active reviews. The card remains in its lesson until you delete it
          there.
        </p>
        {failed ? (
          <p className="field-error" role="alert">
            The card was not archived. Try again or keep reviewing.
          </p>
        ) : null}
        <div className="dialog-actions">
          <button
            ref={cancelRef}
            type="button"
            className="soft-button"
            disabled={pending}
            onClick={onCancel}
          >
            Keep reviewing
          </button>
          <button type="button" className="danger-button" disabled={pending} onClick={onConfirm}>
            {pending ? "Archiving…" : failed ? "Try archiving again" : "Archive card"}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function CollectionStatus({
  collection,
}: {
  collection: FlashcardReviewCollection;
}): React.JSX.Element {
  const statuses = [
    `${String(collection.scheduled)} scheduled`,
    `${String(collection.archived)} archived`,
    `${String(collection.total)} ${collection.total === 1 ? "card" : "cards"} total`,
  ];
  return <span className="flashcards-collection-status">{statuses.join(" · ")}</span>;
}

function applySavedOutcome(
  overview: FlashcardReviewOverview,
  submission: ReviewSubmission,
  reviewVersion: number,
): FlashcardReviewOverview {
  const scheduledDelta =
    submission.outcome === "keep-current" ? 0 : submission.outcome === "archive" ? 0 : 1;
  const archivedDelta = submission.outcome === "archive" ? 1 : 0;
  const readyDelta = submission.outcome === "keep-current" ? 0 : -1;
  const updateSummary = <T extends { ready: number; scheduled: number; archived: number }>(
    value: T,
  ): T => ({
    ...value,
    ready: value.ready + readyDelta,
    scheduled: value.scheduled + scheduledDelta,
    archived: value.archived + archivedDelta,
  });
  return {
    summary: updateSummary(overview.summary),
    collections: overview.collections.map((collection) =>
      collection.id === submission.card.collectionId ? updateSummary(collection) : collection,
    ),
    readyCards:
      submission.outcome === "keep-current"
        ? overview.readyCards.map((card) =>
            card.id === submission.card.id ? { ...card, reviewVersion } : card,
          )
        : overview.readyCards.filter((card) => card.id !== submission.card.id),
  };
}

function collectionRefKey(collectionId: string | null): string {
  return collectionId ?? "uncollected";
}

function removeOrganizeError<T extends Record<string, string | undefined>>(
  errors: T,
  field: keyof T,
): T {
  const next = { ...errors };
  delete next[field];
  return next;
}

function outcomeMessage(outcome: FlashcardReviewOutcome, question: string): string {
  if (outcome === "keep-current") return `Kept current: ${question}`;
  if (outcome === "archive") return `Archived: ${question}`;
  if (outcome === "one-week") return `Scheduled in one week: ${question}`;
  if (outcome === "one-month") return `Scheduled in one month: ${question}`;
  return `Scheduled in three months: ${question}`;
}
